import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { orgApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2, RotateCcw, CheckCircle2, Loader2, Shield } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [eraseStep, setEraseStep] = useState(0); // 0=closed, 1=warning, 2=typed confirm
  const [eraseConfirmText, setEraseConfirmText] = useState("");
  const [erasing, setErasing] = useState(false);
  const [eraseResult, setEraseResult] = useState(null);

  const [resetStep, setResetStep] = useState(0);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const handleEraseAll = async () => {
    setErasing(true);
    try {
      const res = await orgApi.resetAllData("ERASE ALL DATA");
      setEraseResult(res.data);
      setEraseStep(0);
      setEraseConfirmText("");
    } catch (e) {
      console.error(e);
      setEraseResult({ status: "error", message: e.response?.data?.detail || "Failed to erase data" });
    } finally { setErasing(false); }
  };

  const handleResetTruth = async () => {
    setResetting(true);
    try {
      const res = await orgApi.resetDerivedTruth("RESET TRUTH");
      setResetResult(res.data);
      setResetStep(0);
      setResetConfirmText("");
    } catch (e) {
      console.error(e);
      setResetResult({ status: "error", message: e.response?.data?.detail || "Failed to reset truth" });
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-barlow font-bold text-2xl text-slate-50">Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your workspace and data</p>
      </div>

      {/* Success result banners */}
      {eraseResult?.status === "erased" && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5" data-testid="erase-success">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            <div>
              <h3 className="text-base font-barlow font-bold text-emerald-400">Company data erased successfully</h3>
              <p className="text-xs text-slate-400 mt-0.5">Your workspace has been reset. You can now upload new data and start from a clean state.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
            {Object.entries(eraseResult.deleted_counts || {}).map(([label, count]) => (
              <div key={label} className="text-xs flex justify-between px-3 py-1.5 rounded bg-slate-800/30">
                <span className="text-slate-500">{label}</span>
                <span className="text-slate-300 font-mono">{count}</span>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button onClick={() => navigate("/dashboard/data-pool")} className="bg-cyan-600 hover:bg-cyan-500 gap-2 text-xs" data-testid="upload-after-erase">
              Upload New Files
            </Button>
          </div>
        </div>
      )}

      {resetResult?.status === "reset" && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5" data-testid="reset-success">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            <div>
              <h3 className="text-base font-barlow font-bold text-emerald-400">Derived truth reset successfully</h3>
              <p className="text-xs text-slate-400 mt-0.5">Your uploaded records remain. Re-run the AI Diagnostic to re-analyse.</p>
            </div>
          </div>
        </div>
      )}

      {(eraseResult?.status === "error" || resetResult?.status === "error") && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
          <span className="text-sm text-red-400">{eraseResult?.message || resetResult?.message}</span>
        </div>
      )}

      {/* ═══════════════════════════════════════
          DANGER ZONE
          ═══════════════════════════════════════ */}
      <Card className="bg-slate-900/40 border-red-500/20" data-testid="danger-zone">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-barlow font-bold text-red-400">Danger Zone</h2>
          </div>
          <p className="text-xs text-slate-500 mb-6">
            These actions are destructive and cannot be undone. Proceed with caution.
          </p>

          <div className="space-y-4">
            {/* Reset Derived Truth */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-amber-500/15 bg-amber-500/[0.02]" data-testid="reset-truth-section">
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-slate-200">Reset Derived Truth Only</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Erases diagnostic runs, approvals, ledger entries, and reports. Keeps your original uploaded records for re-analysis.
                </p>
              </div>
              <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 shrink-0"
                onClick={() => setResetStep(1)} data-testid="reset-truth-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Reset Truth
              </Button>
            </div>

            {/* Erase All Data */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-red-500/20 bg-red-500/[0.02]" data-testid="erase-all-section">
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-red-400" />
                  <h3 className="text-sm font-semibold text-slate-200">Erase All Data</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Permanently delete uploaded files, pooled records, diagnostic runs, ledger entries, vault allocations, reports, and all generated financial truth data for this company.
                </p>
              </div>
              <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                onClick={() => setEraseStep(1)} data-testid="erase-all-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Erase All Data
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════
          ERASE ALL DATA — Step 1: Warning Modal
          ═══════════════════════════════════════ */}
      <Dialog open={eraseStep === 1} onOpenChange={() => setEraseStep(0)}>
        <DialogContent className="bg-[#0B1120] border-red-500/20 max-w-md" data-testid="erase-warning-modal">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2 font-barlow">
              <AlertTriangle className="w-5 h-5" /> Permanently erase all company data?
            </DialogTitle>
            <DialogDescription className="text-slate-400 mt-3 text-sm leading-relaxed">
              This action will permanently remove all uploaded financial files, pooled records, diagnostic history, vault ledger entries, reports, and derived dashboard truth for this company.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-red-500/[0.06] border border-red-500/15 mt-2">
            <p className="text-xs text-red-400 font-semibold flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> This cannot be undone.
            </p>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setEraseStep(0)} className="border-slate-700">Cancel</Button>
            <Button onClick={() => { setEraseStep(2); setEraseConfirmText(""); }}
              className="bg-red-600 hover:bg-red-500 text-white" data-testid="erase-continue-btn">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════
          ERASE ALL DATA — Step 2: Typed Confirmation
          ═══════════════════════════════════════ */}
      <Dialog open={eraseStep === 2} onOpenChange={() => setEraseStep(0)}>
        <DialogContent className="bg-[#0B1120] border-red-500/20 max-w-md" data-testid="erase-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2 font-barlow">
              <Trash2 className="w-5 h-5" /> Final Confirmation
            </DialogTitle>
            <DialogDescription className="text-slate-400 mt-2 text-sm">
              Type <span className="text-red-400 font-mono font-bold">ERASE ALL DATA</span> to confirm permanent deletion.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <Label className="text-xs text-slate-400">Confirmation phrase</Label>
            <Input value={eraseConfirmText} onChange={(e) => setEraseConfirmText(e.target.value)}
              placeholder="Type ERASE ALL DATA"
              className="bg-slate-900/50 border-red-500/20 mt-1 font-mono text-sm text-slate-200"
              data-testid="erase-confirm-input" />
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setEraseStep(0)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleEraseAll}
              disabled={eraseConfirmText !== "ERASE ALL DATA" || erasing}
              className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="erase-final-btn">
              {erasing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Erasing...</> : "Permanently Erase"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════
          RESET TRUTH — Step 1: Warning
          ═══════════════════════════════════════ */}
      <Dialog open={resetStep === 1} onOpenChange={() => setResetStep(0)}>
        <DialogContent className="bg-[#0B1120] border-amber-500/20 max-w-md" data-testid="reset-warning-modal">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2 font-barlow">
              <RotateCcw className="w-5 h-5" /> Reset derived financial truth?
            </DialogTitle>
            <DialogDescription className="text-slate-400 mt-3 text-sm leading-relaxed">
              This will erase all diagnostic runs, approvals, vault ledger entries, and reports. Your original uploaded records will remain for re-analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <Label className="text-xs text-slate-400">Type <span className="text-amber-400 font-mono font-bold">RESET TRUTH</span> to confirm</Label>
            <Input value={resetConfirmText} onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="Type RESET TRUTH"
              className="bg-slate-900/50 border-amber-500/20 mt-1 font-mono text-sm text-slate-200"
              data-testid="reset-confirm-input" />
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setResetStep(0)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleResetTruth}
              disabled={resetConfirmText !== "RESET TRUTH" || resetting}
              className="bg-amber-600 hover:bg-amber-500 text-slate-900 disabled:opacity-30"
              data-testid="reset-final-btn">
              {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Resetting...</> : "Reset Truth"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
